import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { Bell } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityScope } from '@/hooks/useCommunityScope';
import {
  resolveNotificationPreferences,
  NotificationPreferenceKey,
  getNotificationTypePreferenceKeysForRole,
} from '@/data/notificationPreferences';
import {
  getUserNotifications,
  getNotificationsByCommunity,
  markUserNotificationsAsRead,
  updateUserLastNotification,
} from '@/services/notificationService';
import { syncPushSubscriptionForUser } from '@/services/pushNotificationService';
import type { NotificationItem, NotificationSourceType } from '@/types/notifications';
import { cn } from '@/lib/utils';
import { parseNotificationId } from '@/utils/notifications';
import { resolvePrimaryCommunityId } from '@/utils/communityScope';

type NotificationWithState = NotificationItem & { isRead: boolean };

type NotificationSections = {
  community: NotificationWithState[];
  user: NotificationWithState[];
};

type CurrentUser = ReturnType<typeof useAuth>['currentUser'];

const PREFERENCE_TO_NOTIFICATION_TYPE: Array<{
  preference: NotificationPreferenceKey;
  type: NotificationSourceType;
}> = [
  { preference: 'notiEvents', type: 'EVENT' },
  { preference: 'notiPayments', type: 'PAYMENT' },
  { preference: 'notiIncidents', type: 'INCIDENT' },
  { preference: 'notiMeetings', type: 'MEETING' },
  { preference: 'notiAgreements', type: 'AGREEMENT' },
  { preference: 'notiNotices', type: 'NOTICE' },
  { preference: 'notiPolls', type: 'POLL' },
  { preference: 'notiProposals', type: 'PROPOSAL' },
  { preference: 'notiDocuments', type: 'DOCUMENT' },
];

const NOTIFICATION_TYPE_LABELS: Record<NotificationSourceType, string> = {
  EVENT: 'Evento',
  PAYMENT: 'Pago',
  INCIDENT: 'Incidencia',
  MEETING: 'Reunión',
  AGREEMENT: 'Acuerdo',
  NOTICE: 'Aviso',
  POLL: 'Votación',
  PROPOSAL: 'Propuesta',
  DOCUMENT: 'Documento',
  USER_NOTIFICATION: 'Notificación',
};

const NOTIFICATION_DESTINATIONS: Partial<
  Record<NotificationSourceType, { path: string; state?: Record<string, unknown> }>
> = {
  EVENT: { path: '/app/calendar' },
  PAYMENT: { path: '/app/payments' },
  INCIDENT: { path: '/app/incidents' },
  MEETING: { path: '/app/meetings' },
  AGREEMENT: { path: '/app/meetings', state: { notificationTab: 'agreements' } },
  NOTICE: { path: '/app/announcements' },
  POLL: { path: '/app/quick-polls' },
  PROPOSAL: { path: '/app/suggestions' },
  DOCUMENT: { path: '/app/documents' },
};

const USER_NOTIFICATION_DESTINATIONS: Record<string, { path: string; state?: Record<string, unknown> }> = {
  incident_updated: { path: '/app/incidents' },
  reservation_reminder: { path: '/app/reservations' },
  document_expiration_warning: { path: '/app/documents' },
  document_due_notice: { path: '/app/documents' },
  support_updated: { path: '/app/support' },
  support_created: { path: '/app/support' },
  payment_created: { path: '/app/payments' },
  payment_updated: { path: '/app/payments' },
  security_alert: { path: '/app/admin/audit-logs' },
  gdpr_rights_request: { path: '/app/users' },
  gdpr_rights_resolved: { path: '/app/profile' },
  account_closure_notice: { path: '/app/communities' },
};

const USER_NOTIFICATION_LABELS: Record<string, string> = {
  incident_updated: NOTIFICATION_TYPE_LABELS.INCIDENT,
  reservation_reminder: 'Reserva',
  document_expiration_warning: 'Documento',
  document_due_notice: 'Documento',
  support_updated: 'Soporte',
  support_created: 'Soporte',
  payment_created: 'Pago',
  payment_updated: 'Pago',
  security_alert: 'Seguridad',
  gdpr_rights_request: 'Derechos RGPD',
  gdpr_rights_resolved: 'Derechos RGPD',
  account_closure_notice: 'Baja Vecino (Art. 9.1.h LPH)',
};

const USER_NOTIFICATION_ICONS: Record<string, string> = {
  incident_updated: '⚠️',
  reservation_reminder: '📅',
  document_expiration_warning: '📄',
  document_due_notice: '📄',
  support_updated: '💬',
  support_created: '🎫',
  payment_created: '💳',
  payment_updated: '💳',
  security_alert: '🚨',
  gdpr_rights_request: '⚖️',
  gdpr_rights_resolved: '⚖️',
  account_closure_notice: '🚪',
};

const resolveLastSeenNotificationId = (user: CurrentUser) => {
  if (!user) {
    return null;
  }

  const camelCaseValue = parseNotificationId(user.notiLast);
  if (camelCaseValue !== null) {
    return camelCaseValue;
  }

  return parseNotificationId(user.noti_last);
};

const mergeNotificationGroup = (
  previousNotifications: NotificationWithState[],
  fetchedNotifications: NotificationItem[],
): NotificationWithState[] => {
  const previousById = new Map(
    previousNotifications.map((notification) => [notification.id, notification]),
  );

  const mergedMap = new Map<number, NotificationWithState>();

  fetchedNotifications.forEach((notification) => {
    const previous = previousById.get(notification.id);
    mergedMap.set(notification.id, {
      ...notification,
      isRead: previous?.isRead ?? false,
    });
  });

  previousNotifications.forEach((notification) => {
    if (!mergedMap.has(notification.id)) {
      mergedMap.set(notification.id, notification);
    }
  });

  return Array.from(mergedMap.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
};

export const NotificationBell: FC = () => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    activeCommunityId,
    activeCommunityIds,
    availableCommunities,
  } = useCommunityScope();

  const currentUserId = currentUser?.id ?? null;
  const isAdmin = currentUser?.role === 'admin';

  const preferences = useMemo(() => resolveNotificationPreferences(currentUser), [currentUser]);
  const allowedNotificationTypeKeys = useMemo(
    () => getNotificationTypePreferenceKeysForRole(currentUser?.role ?? null),
    [currentUser?.role],
  );
  const hasExternalPushEnabled = preferences.notiPush;

  const scopedCommunityIds = useMemo(() => {
    if (isAdmin) {
      if (activeCommunityId) {
        return [activeCommunityId];
      }
      if (activeCommunityIds.length > 0) {
        return activeCommunityIds;
      }
      if (availableCommunities.length > 0) {
        return availableCommunities.map((community) => community.id);
      }
      const primaryId = resolvePrimaryCommunityId(currentUser);
      return primaryId ? [primaryId] : [];
    }

    if (activeCommunityId) {
      return [activeCommunityId];
    }

    const primaryId = resolvePrimaryCommunityId(currentUser);
    return primaryId ? [primaryId] : [];
  }, [activeCommunityId, activeCommunityIds, availableCommunities, currentUser, isAdmin]);

  const resolvedLastSeenId = useMemo(
    () => resolveLastSeenNotificationId(currentUser),
    [currentUser],
  );

  const [lastSeenNotificationId, setLastSeenNotificationId] = useState<number | null>(resolvedLastSeenId);

  useEffect(() => {
    setLastSeenNotificationId(resolvedLastSeenId);
  }, [resolvedLastSeenId]);

  const activeNotificationTypes = useMemo(() => {
    if (allowedNotificationTypeKeys.length === 0) {
      return [];
    }

    const allowedKeySet = new Set<NotificationPreferenceKey>(allowedNotificationTypeKeys);

    return PREFERENCE_TO_NOTIFICATION_TYPE
      .filter(({ preference }) => allowedKeySet.has(preference) && preferences[preference] === true)
      .map(({ type }) => type);
  }, [allowedNotificationTypeKeys, preferences]);

  const shouldFetchNotifications = Boolean(currentUserId);

  const {
    data: fetchedNotifications,
    isLoading,
    isError,
    error,
  } = useQuery<{
    community: NotificationItem[];
    user: NotificationItem[];
  }>({
    queryKey: [
      'notifications',
      scopedCommunityIds,
      activeNotificationTypes,
      lastSeenNotificationId,
      currentUserId,
    ],
    queryFn: async () => {
      if (!currentUserId) {
        return { community: [], user: [] };
      }

      const communityNotificationsPromise =
        !isAdmin && scopedCommunityIds.length > 0 && activeNotificationTypes.length > 0
          ? getNotificationsByCommunity(scopedCommunityIds, activeNotificationTypes, {
              afterId: lastSeenNotificationId ?? undefined,
              currentUserId,
            })
          : Promise.resolve([]);

      const primaryCommunityId = activeCommunityId ?? scopedCommunityIds[0] ?? null;
      const userNotificationsPromise = getUserNotifications(currentUserId, {
        communityId: primaryCommunityId,
        filterByCommunity: Boolean(!isAdmin && activeCommunityId),
      });

      const [communityNotifications, userNotifications] = await Promise.all([
        communityNotificationsPromise,
        userNotificationsPromise,
      ]);

      const sortByDateDesc = (items: NotificationItem[]) =>
        [...items].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

      return {
        community: sortByDateDesc(communityNotifications),
        user: sortByDateDesc(userNotifications),
      };
    },
    enabled: shouldFetchNotifications,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const [notificationSections, setNotificationSections] = useState<NotificationSections>({
    community: [],
    user: [],
  });

  useEffect(() => {
    if (!fetchedNotifications) {
      return;
    }

    setNotificationSections((previous) => ({
      community: mergeNotificationGroup(previous.community, fetchedNotifications.community),
      user: mergeNotificationGroup(previous.user, fetchedNotifications.user),
    }));
  }, [fetchedNotifications]);

  useEffect(() => {
    if (!currentUserId || !hasExternalPushEnabled) {
      return;
    }

    syncPushSubscriptionForUser(currentUserId).catch((pushSyncError: unknown) => {
      console.warn('No se pudo sincronizar la suscripción push del usuario:', pushSyncError);
    });
  }, [currentUserId, hasExternalPushEnabled]);

  const unreadCount = useMemo(() => {
    const communityUnread = notificationSections.community.filter(
      (notification) => !notification.isRead,
    ).length;
    const userUnread = notificationSections.user.filter((notification) => !notification.isRead).length;
    return communityUnread + userUnread;
  }, [notificationSections]);

  const revertNotificationReadState = useCallback((notification: NotificationWithState) => {
    setNotificationSections((previous) => {
      const key = notification.sourceType === 'USER_NOTIFICATION' ? 'user' : 'community';
      return {
        ...previous,
        [key]: previous[key].map((item) =>
          item.id === notification.id ? { ...item, isRead: false } : item,
        ),
      } satisfies NotificationSections;
    });
  }, []);

  const { mutate: persistLastSeenNotification, isPending: isPersistingLastSeen } = useMutation({
    mutationFn: async (lastId: number) => {
      if (!currentUserId) {
        throw new Error('No se pudo identificar al usuario actual.');
      }
      await updateUserLastNotification(currentUserId, lastId);
      return lastId;
    },
    onSuccess: (lastId) => {
      setLastSeenNotificationId((previous) => {
        if (previous === null) {
          return lastId;
        }
        return Math.max(previous, lastId);
      });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (mutationError: unknown) => {
      const message = mutationError instanceof Error
        ? mutationError.message
        : 'No se pudieron marcar las notificaciones como leídas.';
      toast({
        title: 'Error al actualizar',
        description: message,
        variant: 'destructive',
        duration: 4000,
      });
    },
  });

  const {
    mutateAsync: mutateUserNotificationReads,
    isPending: isMarkingUserNotifications,
  } = useMutation({
    mutationFn: async ({
      userId,
      notificationIds,
    }: {
      userId: string;
      notificationIds: string[];
    }) => {
      return markUserNotificationsAsRead(userId, notificationIds);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const persistUserNotificationReads = useCallback(
    async (
      notificationIds: string[],
      options?: { onRevert?: () => void },
    ) => {
      const uniqueIds = Array.from(
        new Set(
          notificationIds
            .map((id) => (typeof id === 'string' ? id.trim() : ''))
            .filter((id) => id.length > 0),
        ),
      );

      if (uniqueIds.length === 0) {
        return [] as string[];
      }

      if (!currentUserId) {
        options?.onRevert?.();
        const error = new Error('No se pudo identificar al usuario actual.');
        toast({
          title: 'Error al actualizar',
          description: error.message,
          variant: 'destructive',
          duration: 4000,
        });
        throw error;
      }

      try {
        const updatedIds = await mutateUserNotificationReads({
          userId: currentUserId,
          notificationIds: uniqueIds,
        });

        if (!updatedIds || updatedIds.length === 0) {
          throw new Error('No se pudieron marcar las notificaciones como leídas.');
        }

        setNotificationSections((previous) => ({
          ...previous,
          user: previous.user.map((notification) =>
            updatedIds.includes(notification.sourceId)
              ? { ...notification, isRead: true }
              : notification,
          ),
        }));

        return updatedIds;
      } catch (mutationError: unknown) {
        options?.onRevert?.();
        const message = mutationError instanceof Error
          ? mutationError.message
          : 'No se pudieron marcar las notificaciones como leídas.';
        toast({
          title: 'Error al actualizar',
          description: message,
          variant: 'destructive',
          duration: 4000,
        });
        throw mutationError instanceof Error ? mutationError : new Error(message);
      }
    },
    [currentUserId, mutateUserNotificationReads, setNotificationSections, toast],
  );

  const markNotificationAsRead = useCallback(
    (notification: NotificationWithState) => {
      setNotificationSections((previous) => {
        const key = notification.sourceType === 'USER_NOTIFICATION' ? 'user' : 'community';
        return {
          ...previous,
          [key]: previous[key].map((item) =>
            item.id === notification.id ? { ...item, isRead: true } : item,
          ),
        } satisfies NotificationSections;
      });

      if (notification.sourceType !== 'USER_NOTIFICATION') {
        return;
      }

      const notificationId = typeof notification.sourceId === 'string' ? notification.sourceId : '';
      if (!notificationId) {
        return;
      }

      void persistUserNotificationReads([notificationId], {
        onRevert: () => {
          revertNotificationReadState(notification);
        },
      }).catch(() => {
        // El manejo de errores ya se realiza dentro de persistUserNotificationReads.
      });
    },
    [persistUserNotificationReads, revertNotificationReadState],
  );

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const payload: unknown = event.data;

      if (!payload || typeof payload !== 'object') {
        return;
      }

      const record = payload as Record<string, unknown>;

      if (record.type !== 'notification-click') {
        return;
      }

      const parsedId = parseNotificationId(
        record.notificationId
        ?? record.notification_id
        ?? record.id,
      );

      if (parsedId === null) {
        return;
      }

      const notification =
        notificationSections.community.find((item) => item.id === parsedId)
        ?? notificationSections.user.find((item) => item.id === parsedId)
        ?? null;

      if (!notification) {
        return;
      }

      markNotificationAsRead(notification);

      if (notification.sourceType === 'USER_NOTIFICATION') {
        return;
      }

      const currentLastSeen = lastSeenNotificationId ?? 0;
      const candidateLastSeen = Math.max(parsedId, currentLastSeen);

      if (candidateLastSeen <= currentLastSeen) {
        return;
      }

      persistLastSeenNotification(candidateLastSeen, {
        onError: () => {
          revertNotificationReadState(notification);
        },
      });
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [
    currentUserId,
    lastSeenNotificationId,
    markNotificationAsRead,
    notificationSections,
    persistLastSeenNotification,
    revertNotificationReadState,
  ]);

  const getNotificationDestination = useCallback(
    (
      notification: NotificationWithState,
    ): { path: string; state?: Record<string, unknown> } | null => {
      if (notification.sourceType === 'USER_NOTIFICATION') {
        const subtype = (notification.userNotificationType ?? '').trim();
        if (subtype && USER_NOTIFICATION_DESTINATIONS[subtype]) {
          return USER_NOTIFICATION_DESTINATIONS[subtype];
        }
        return null;
      }

      const destination = NOTIFICATION_DESTINATIONS[notification.sourceType];
      return destination ?? null;
    },
    [],
  );

  const handleNotificationClick = useCallback(
    (notification: NotificationWithState) => {
      markNotificationAsRead(notification);

      const destination = getNotificationDestination(notification);

      if (notification.sourceType === 'USER_NOTIFICATION') {
        if (destination) {
          navigate(destination.path, destination.state ? { state: destination.state } : undefined);
        }
        return;
      }

      const currentLastSeen = lastSeenNotificationId ?? 0;
      const candidateLastSeen = Math.max(notification.id, currentLastSeen);

      if (candidateLastSeen > currentLastSeen) {
        persistLastSeenNotification(candidateLastSeen, {
          onError: () => {
            revertNotificationReadState(notification);
          },
        });
      }

      if (destination) {
        navigate(destination.path, destination.state ? { state: destination.state } : undefined);
      }
    },
    [
      getNotificationDestination,
      lastSeenNotificationId,
      markNotificationAsRead,
      navigate,
      persistLastSeenNotification,
      revertNotificationReadState,
    ],
  );

  const markAllAsRead = useCallback(() => {
    const hasCommunityNotifications = notificationSections.community.length > 0;
    const hasUserNotifications = notificationSections.user.length > 0;

    if (!hasCommunityNotifications && !hasUserNotifications) {
      return;
    }

    const latestCommunityId = notificationSections.community.reduce(
      (max, notification) => (notification.id > max ? notification.id : max),
      lastSeenNotificationId ?? 0,
    );

    const userNotificationIds = notificationSections.user
      .filter((notification) => notification.sourceType === 'USER_NOTIFICATION' && !notification.isRead)
      .map((notification) => notification.sourceId);

    const previousCommunityState = notificationSections.community;
    const previousUserState = notificationSections.user;

    setNotificationSections((previous) => ({
      community: previous.community.map((notification) => ({ ...notification, isRead: true })),
      user: previous.user.map((notification) => ({ ...notification, isRead: true })),
    }));

    const shouldPersistCommunity = latestCommunityId > (lastSeenNotificationId ?? 0);
    const shouldMarkUserNotifications = userNotificationIds.length > 0;

    let pendingOperations = 0;
    if (shouldPersistCommunity) {
      pendingOperations += 1;
    }
    if (shouldMarkUserNotifications) {
      pendingOperations += 1;
    }

    if (pendingOperations === 0) {
      toast({
        title: 'Notificaciones actualizadas',
        description: 'Todas las notificaciones han sido marcadas como leídas.',
        duration: 3000,
      });
      return;
    }

    let completedOperations = 0;
    const handleOperationSuccess = () => {
      completedOperations += 1;
      if (completedOperations === pendingOperations) {
        toast({
          title: 'Notificaciones actualizadas',
          description: 'Todas las notificaciones han sido marcadas como leídas.',
          duration: 3000,
        });
      }
    };

    if (shouldPersistCommunity) {
      persistLastSeenNotification(latestCommunityId, {
        onError: () => {
          setNotificationSections((previous) => ({
            ...previous,
            community: previousCommunityState,
          }));
        },
        onSuccess: () => {
          handleOperationSuccess();
        },
      });
    }

    if (shouldMarkUserNotifications) {
      void persistUserNotificationReads(userNotificationIds, {
        onRevert: () => {
          setNotificationSections((previous) => ({
            ...previous,
            user: previousUserState,
          }));
        },
      })
        .then(() => {
          handleOperationSuccess();
        })
        .catch(() => {
          // El manejo de errores ya se realiza dentro de persistUserNotificationReads.
        });
    }
  }, [
    lastSeenNotificationId,
    notificationSections,
    persistLastSeenNotification,
    persistUserNotificationReads,
    toast,
  ]);

  const getNotificationIcon = (notification: NotificationWithState) => {
    if (notification.sourceType === 'USER_NOTIFICATION') {
      const subtype = (notification.userNotificationType ?? '').trim();
      if (subtype && USER_NOTIFICATION_ICONS[subtype]) {
        return USER_NOTIFICATION_ICONS[subtype];
      }
      return '🔔';
    }

    switch (notification.type) {
      case 'payment':
        return '💳';
      case 'incident':
        return '⚠️';
      case 'meeting':
        return '📅';
      case 'agreement':
        return '🤝';
      case 'notice':
        return '📢';
      case 'poll':
        return '🗳️';
      case 'proposal':
        return '📝';
      case 'event':
        return '🎉';
      default:
        return '🔔';
    }
  };

  const getNotificationTypeLabel = (notification: NotificationWithState) => {
    if (notification.sourceType === 'USER_NOTIFICATION') {
      const subtype = (notification.userNotificationType ?? '').trim();
      if (subtype && USER_NOTIFICATION_LABELS[subtype]) {
        return USER_NOTIFICATION_LABELS[subtype];
      }
      return NOTIFICATION_TYPE_LABELS.USER_NOTIFICATION;
    }
    return NOTIFICATION_TYPE_LABELS[notification.sourceType] ?? notification.sourceType;
  };

  const hasUnreadNotifications = unreadCount > 0;

  const renderNotificationItems = (items: NotificationWithState[]) =>
    items.map((notification) => (
      <DropdownMenuItem key={notification.id} asChild className="p-0">
        <button
          type="button"
          onClick={() => handleNotificationClick(notification)}
          className={cn(
            'flex w-full flex-col items-start gap-3 p-4 text-left focus:outline-none transition-colors',
            !notification.isRead ? 'bg-primary/5 hover:bg-primary/10 dark:bg-primary/15 dark:hover:bg-primary/20' : 'hover:bg-muted/50',
          )}
        >
          <div className="flex w-full items-start gap-3">
            <span className="text-lg">{getNotificationIcon(notification)}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className={cn("truncate text-sm", !notification.isRead ? "font-semibold text-foreground" : "font-medium text-muted-foreground")}>
                  {notification.title}
                </p>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  {getNotificationTypeLabel(notification)}
                </Badge>
                {!notification.isRead && (
                  <div className="h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                )}
              </div>
              {notification.description && (
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  {notification.description}
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                {formatDistanceToNow(notification.createdAt, {
                  addSuffix: true,
                  locale: es,
                })}
              </p>
            </div>
          </div>
        </button>
      </DropdownMenuItem>
    ));

  const renderNotificationContent = () => {
    if (isLoading) {
      return (
        <div className="p-4 text-center text-muted-foreground">
          Cargando notificaciones...
        </div>
      );
    }

    if (isError) {
      const message = error instanceof Error
        ? error.message
        : 'No se pudieron cargar las notificaciones.';
      return (
        <div className="p-4 text-center text-muted-foreground">
          {message}
        </div>
      );
    }

    const hasCommunityNotifications = notificationSections.community.length > 0;
    const hasUserNotifications = notificationSections.user.length > 0;

    if (!hasCommunityNotifications && !hasUserNotifications) {
      if (isAdmin) {
        return (
          <div className="p-4 text-center text-muted-foreground">
            No tienes tickets ni notificaciones pendientes.
          </div>
        );
      }

      if (scopedCommunityIds.length === 0) {
        return (
          <div className="p-4 text-center text-muted-foreground">
            Asóciate a una comunidad para recibir notificaciones.
          </div>
        );
      }

      if (activeNotificationTypes.length === 0) {
        return (
          <div className="p-4 text-center text-muted-foreground">
            No tienes tipos de notificación activos. Actívalos en tus preferencias.
          </div>
        );
      }

      return (
        <div className="p-4 text-center text-muted-foreground">
          No tienes notificaciones.
        </div>
      );
    }

    return (
      <ScrollArea className="max-h-80" viewportClassName="max-h-80 overflow-y-auto pr-2">
        {hasCommunityNotifications && (
          <div className="py-1">
            <div className="px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">
              Comunidad
            </div>
            {renderNotificationItems(notificationSections.community)}
          </div>
        )}
        {hasUserNotifications && (
          <div
            className={cn(
              'py-1',
              hasCommunityNotifications && 'border-t border-muted/20',
            )}
          >
            <div className="px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">
              {isAdmin ? 'Soporte y plataforma' : 'Usuario'}
            </div>
            {renderNotificationItems(notificationSections.user)}
          </div>
        )}
      </ScrollArea>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative border border-border bg-card shadow-none hover:border-primary/40 hover:bg-primary/5"
          aria-label="Abrir panel de notificaciones"
        >
          <Bell className="h-4 w-4" />
          {hasUnreadNotifications && (
            <Badge
              variant="destructive"
              className="absolute -right-2 -top-2 flex h-5 w-5 min-w-[20px] items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-96 w-80 border border-border bg-popover text-popover-foreground shadow-surface"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificaciones</span>
          {hasUnreadNotifications && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs"
              disabled={isPersistingLastSeen || isMarkingUserNotifications}
            >
              Marcar todo como leído
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {renderNotificationContent()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
